/**
 * Calibration statistics for the daily report's Ground Cals and Test Lines pages (TJR Progress Operations Report
 * pages 6–7). Formulas derived from `tjf.json` `cal_data` against the printed 12 and 16 Oct 2026 reports for F26048
 * (tests/calibration.test.ts reproduces every printed figure):
 *
 * Ground cals (one row per used hand-sample/background pair, pre-flight then post-flight, per daily entry):
 *   - Normalised TC/K/U/Th = hand sample − background, channel by channel (683.1 − 198.6 = 484.5).
 *   - ThPeak and % FWHM on every row of an entry are the entry's LAST used `th_peak` result (11 Oct flight 2 has two
 *     used results, 216.59/5.19 then 215.94/4.75; both of its rows print 215.94/4.75). MB chose TJR's rule (Q125 B,
 *     18 Oct 2026) for rows read from a Zdas log as well: the pre and the post row print the flight's last result.
 *   - TH Chg (±3 %) = 100 × (normalised Th − running mean) ÷ running mean, the running mean taken over the rows so
 *     far including the current one (row 2: 471.7 vs mean(484.5, 471.7) = 478.1 → −1.3).
 *   - Diff (5 m max) = horizontal distance of the sample position from the first row's sample position.
 *   - Header: Av Normalised Th = mean of normalised Th; Min/Max = min/max of normalised Th; Standard Deviation =
 *     sample standard deviation (n − 1).
 * Test lines (801 and 802 Th, in order: per entry, 801 before 802):
 *   - TH Chg (±7 %) = the same running-mean change, over all test-line values in order (801 and 802 together).
 *   - Av Normalised Th = mean of all values (raw Th, no background is subtracted); Standard Deviation = sample SD.
 *   - TJR QUIRK: the printed "Min TH" is the minimum of the 801 values only and "Max TH" the maximum of the 802
 *     values only (12 Oct prints Min 154.7 > Max 153.1; 16 Oct prints 154.7 / 156.6 while the values range
 *     146.3–164.0). `minTh`/`maxTh` reproduce the print; `rangeMinTh`/`rangeMaxTh` give the true range.
 *
 * Only live, used (TJR `-1`) and confirmed rows count (`countsInCalStats`): a row proposed from a log counts once the
 * crew confirms it. A row without a daily entry (from a log) is grouped by its flight (`calGroupKey`). Input order
 * matters: pass results in chronological order (by entry date, then flight number); within an entry, rows are
 * re-ordered pre then post and 801 then 802. Limits come from the project (`qcLimits`, decision 13); a row is flagged
 * when Teslyn's running-mean check or Zdas's own check fails (Q123, recommendation A).
 */
import type { CalKind, CalResult, Project, Uuid } from './model.js';

/**
 * QC limits for the thorium change of hand samples (ground cals) and test lines, % (MB decision 13: from the
 * project's contract; F26048 3 % and 7 % "of the daily average", read as the running mean that TJR prints and that
 * the tech types into the Zdas job file, Q123 recommendation A).
 */
export interface QcLimits {
  readonly handSamplePct: number;
  readonly testLinePct: number;
  /** Hand-sample position limit, m (TJR "Pos Max Change"; Q107 open, 5 m until the contract sets it). */
  readonly positionM: number;
}

export const DEFAULT_QC_LIMITS: QcLimits = { handSamplePct: 3, testLinePct: 7, positionM: 5 };

/** The project's QC limits; Teslyn's default for each one not set (null, absent or not a positive number). */
export function qcLimits(
  project: Pick<Project, 'qc_hand_sample_pct' | 'qc_test_line_pct'> | null | undefined,
): QcLimits {
  const pick = (v: number | null | undefined, d: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : d;
  return {
    handSamplePct: pick(project?.qc_hand_sample_pct, DEFAULT_QC_LIMITS.handSamplePct),
    testLinePct: pick(project?.qc_test_line_pct, DEFAULT_QC_LIMITS.testLinePct),
    positionM: DEFAULT_QC_LIMITS.positionM,
  };
}

/** A row that counts in the statistics, the Zdas averages and the report: live, used and confirmed. */
export function countsInCalStats(r: CalResult): boolean {
  return r.used && r.deleted_at === null && (r.review_state ?? 'confirmed') === 'confirmed';
}

/** The key results are grouped by: the daily entry, else the flight (log rows before their entry exists). */
export function calGroupKey(r: Pick<CalResult, 'id' | 'daily_entry_id' | 'flight_id'>): string {
  return r.daily_entry_id ?? r.flight_id ?? r.id;
}

/** The phase a log row belongs to (`values_json.phase`); null for TJR imports. */
export function calPhase(r: CalResult): 'pre' | 'post' | null {
  const p = r.values_json['phase'];
  return p === 'pre' || p === 'post' ? p : null;
}

export interface Channels {
  readonly tc: number;
  readonly k: number;
  readonly u: number;
  readonly th: number;
}

export interface GroundCalRow {
  /** The row's group (`calGroupKey`): its daily entry id, else its flight id. */
  readonly daily_entry_id: Uuid;
  readonly flight_id: Uuid | null;
  /** The hand-sample and background results of the pair. */
  readonly sample_id: Uuid;
  readonly background_id: Uuid;
  readonly phase: 'pre' | 'post';
  readonly easting: number | null;
  readonly northing: number | null;
  readonly altitude_m: number | null;
  readonly sample: Channels;
  readonly background: Channels;
  readonly normalised: Channels;
  readonly th_peak: number | null;
  readonly fwhm_pct: number | null;
  /** Change of normalised Th from the running mean, % (tolerance: the hand-sample limit, 3 % by default). */
  readonly thChgPct: number;
  /** Distance from the first row's position, metres (tolerance 5 m); null without coordinates. */
  readonly posDiffM: number | null;
  /** |TH Chg| is beyond the hand-sample limit (Teslyn's running-mean check). */
  readonly thChgFail: boolean;
  /**
   * The row is flagged: TH Chg failed. Zdas's own comparison ("[Pass]"/"[Fail]", `CalResult.zdas_pass`) is kept in
   * the data but flags nothing (MB's R29, 24 Oct 2026: TH Chg is the real check; it replaced Q123 A's "either").
   */
  readonly fail: boolean;
}

export interface CalStats {
  readonly avgNormTh: number;
  readonly minTh: number;
  readonly maxTh: number;
  /** Sample standard deviation (n − 1); 0 for fewer than two values. */
  readonly sd: number;
  readonly count: number;
}

export interface GroundCalStats extends CalStats {
  readonly rows: readonly GroundCalRow[];
}

export interface TestLineRow {
  /** The row's group (`calGroupKey`). */
  readonly daily_entry_id: Uuid;
  readonly flight_id: Uuid | null;
  readonly result_id: Uuid;
  readonly line: 801 | 802;
  readonly values: Channels;
  /** Change of Th from the running mean of all test-line values, % (tolerance: the test-line limit, 7 % by default). */
  readonly thChgPct: number;
  readonly thChgFail: boolean;
  /** The row is flagged: TH Chg failed (Zdas's own check flags nothing, R29). */
  readonly fail: boolean;
}

export interface TestLineStats extends CalStats {
  /** True minimum and maximum over every value (TJR's printed min/max are per line, see the header note). */
  readonly rangeMinTh: number;
  readonly rangeMaxTh: number;
  readonly rows: readonly TestLineRow[];
}

/** Normalised channels: hand sample minus background. */
export function normalise(sample: Channels, background: Channels): Channels {
  return {
    tc: sample.tc - background.tc,
    k: sample.k - background.k,
    u: sample.u - background.u,
    th: sample.th - background.th,
  };
}

function channels(r: CalResult): Channels {
  return { tc: r.tc ?? 0, k: r.k ?? 0, u: r.u ?? 0, th: r.th ?? 0 };
}

/** Group counted results by daily entry (else flight), keeping the order in which groups first appear. */
function byEntry(results: readonly CalResult[]): CalResult[][] {
  const groups = new Map<string, CalResult[]>();
  for (const r of results) {
    if (!countsInCalStats(r)) continue;
    const key = calGroupKey(r);
    const g = groups.get(key) ?? [];
    g.push(r);
    groups.set(key, g);
  }
  return [...groups.values()];
}

/** % change of each value from the running mean of the values so far (current included). */
function runningChange(values: readonly number[]): number[] {
  let sum = 0;
  return values.map((v, i) => {
    sum += v;
    const mean = sum / (i + 1);
    return mean === 0 ? 0 : (100 * (v - mean)) / mean;
  });
}

function stats(values: readonly number[]): CalStats {
  const n = values.length;
  if (n === 0) return { avgNormTh: 0, minTh: 0, maxTh: 0, sd: 0, count: 0 };
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const variance = n > 1 ? values.reduce((a, v) => a + (v - mean) ** 2, 0) / (n - 1) : 0;
  return {
    avgNormTh: mean,
    minTh: Math.min(...values),
    maxTh: Math.max(...values),
    sd: Math.sqrt(variance),
    count: n,
  };
}

const PHASES: ReadonlyArray<{ phase: 'pre' | 'post'; sample: CalKind; background: CalKind }> = [
  { phase: 'pre', sample: 'th_sample_pre', background: 'background_pre' },
  { phase: 'post', sample: 'th_sample_post', background: 'background_post' },
];

export function groundCalStats(
  results: readonly CalResult[],
  limits: QcLimits = DEFAULT_QC_LIMITS,
): GroundCalStats {
  const pending: Omit<GroundCalRow, 'thChgPct' | 'posDiffM' | 'thChgFail' | 'fail'>[] = [];
  for (const group of byEntry(results)) {
    // TJR's rule (MB, Q125 B): every row of the entry prints the entry's last used Th cal result
    const peak = group.filter((r) => r.kind === 'th_peak').at(-1);
    for (const { phase, sample, background } of PHASES) {
      const samples = group.filter((r) => r.kind === sample);
      const backgrounds = group.filter((r) => r.kind === background);
      const pairs = Math.min(samples.length, backgrounds.length);
      for (let i = 0; i < pairs; i++) {
        const s = samples[i]!;
        const b = backgrounds[i]!;
        pending.push({
          daily_entry_id: calGroupKey(s),
          flight_id: s.flight_id,
          sample_id: s.id,
          background_id: b.id,
          phase,
          easting: s.easting,
          northing: s.northing,
          altitude_m: s.altitude_m,
          sample: channels(s),
          background: channels(b),
          normalised: normalise(channels(s), channels(b)),
          th_peak: peak?.th_peak ?? null,
          fwhm_pct: peak?.fwhm_pct ?? null,
        });
      }
    }
  }
  const th = pending.map((r) => r.normalised.th);
  const change = runningChange(th);
  const first = pending[0];
  const rows: GroundCalRow[] = pending.map((r, i) => {
    const thChgFail = Math.abs(change[i]!) > limits.handSamplePct;
    return {
      ...r,
      thChgPct: change[i]!,
      posDiffM:
        first?.easting != null && first.northing != null && r.easting !== null && r.northing !== null
          ? Math.hypot(r.easting - first.easting, r.northing - first.northing)
          : null,
      thChgFail,
      fail: thChgFail,
    };
  });
  return { ...stats(th), rows };
}

export function testLineStats(
  results: readonly CalResult[],
  limits: QcLimits = DEFAULT_QC_LIMITS,
): TestLineStats {
  const ordered: { r: CalResult; line: 801 | 802 }[] = [];
  for (const group of byEntry(results)) {
    for (const r of group) if (r.kind === 'test_801') ordered.push({ r, line: 801 });
    for (const r of group) if (r.kind === 'test_802') ordered.push({ r, line: 802 });
  }
  const th = ordered.map((o) => o.r.th ?? 0);
  const change = runningChange(th);
  const rows: TestLineRow[] = ordered.map((o, i) => {
    const thChgFail = Math.abs(change[i]!) > limits.testLinePct;
    return {
      daily_entry_id: calGroupKey(o.r),
      flight_id: o.r.flight_id,
      result_id: o.r.id,
      line: o.line,
      values: channels(o.r),
      thChgPct: change[i]!,
      thChgFail,
      fail: thChgFail,
    };
  });
  const all = stats(th);
  const of = (line: 801 | 802): number[] => rows.filter((r) => r.line === line).map((r) => r.values.th);
  const t801 = of(801);
  const t802 = of(802);
  return {
    ...all,
    minTh: t801.length > 0 ? Math.min(...t801) : all.minTh,
    maxTh: t802.length > 0 ? Math.max(...t802) : all.maxTh,
    rangeMinTh: all.minTh,
    rangeMaxTh: all.maxTh,
    rows,
  };
}

/** Teslyn's default tolerances (`DEFAULT_QC_LIMITS`); the report prints the project's own (`qcLimits`). */
export const GROUND_CAL_TH_CHANGE_TOLERANCE_PCT = DEFAULT_QC_LIMITS.handSamplePct;
export const TEST_LINE_TH_CHANGE_TOLERANCE_PCT = DEFAULT_QC_LIMITS.testLinePct;
/** A hand-sample position further than this from the first one is an error (Diff "5 max", chart "Error (>5)"). */
export const GROUND_CAL_POSITION_TOLERANCE_M = DEFAULT_QC_LIMITS.positionM;

/**
 * Which minimum and maximum the Test Lines page prints (questions-for-mb.md Q1, open). TJR prints the 801-only minimum
 * and the 802-only maximum (`'tjr_per_line'`); until MB answers, Teslyn prints the true range of all test-line values
 * (`'true_range'`, the assumption recorded with Q1). Flip this one constant to change every report.
 */
export type TestLineRangeMode = 'true_range' | 'tjr_per_line';
export const TEST_LINE_PRINTED_RANGE: TestLineRangeMode = 'true_range';

export function testLinePrintedRange(
  stats: TestLineStats,
  mode: TestLineRangeMode = TEST_LINE_PRINTED_RANGE,
): { minTh: number; maxTh: number } {
  return mode === 'tjr_per_line'
    ? { minTh: stats.minTh, maxTh: stats.maxTh }
    : { minTh: stats.rangeMinTh, maxTh: stats.rangeMaxTh };
}

/**
 * Test-line chart points per daily entry or date (TJR "Test Line Variations"): the entry's 801 and 802 TH Chg, and "Norm Tho",
 * the mean over the entry's test-line values of the running mean at each value. TJR plots exactly this: the vector
 * chart of the 12 Oct 2026 print gives 154.70, 155.48, 155.19 for 10, 11 and 12 Oct. A missing 801 or 802 is null.
 */
export interface TestLineChartPoint {
  /** The group: the daily entry id by default, or what `groupOf` returns (the daily report groups by date). */
  readonly key: string;
  readonly chg801: number | null;
  readonly chg802: number | null;
  readonly normTh: number;
}

export function testLineChartPoints(
  stats: TestLineStats,
  groupOf: (row: TestLineRow) => string = (row) => row.daily_entry_id,
): TestLineChartPoint[] {
  const out: TestLineChartPoint[] = [];
  let sum = 0;
  let n = 0;
  let cur: { id: string; c801: number | null; c802: number | null; means: number[] } | null = null;
  const flush = (): void => {
    if (cur)
      out.push({
        key: cur.id,
        chg801: cur.c801,
        chg802: cur.c802,
        normTh: cur.means.reduce((a, b) => a + b, 0) / cur.means.length,
      });
  };
  for (const r of stats.rows) {
    sum += r.values.th;
    n += 1;
    const key = groupOf(r);
    if (!cur || cur.id !== key) {
      flush();
      cur = { id: key, c801: null, c802: null, means: [] };
    }
    cur.means.push(sum / n);
    if (r.line === 801) cur.c801 = r.thChgPct;
    else cur.c802 = r.thChgPct;
  }
  flush();
  return out;
}

/** Thorium calibration chart points (TJR "Thorium Calibration Results"): every used ThPeak result in input order. */
export interface ThoriumPeakPoint {
  /** The result's group (`calGroupKey`). */
  readonly daily_entry_id: Uuid;
  readonly th_peak: number;
  readonly fwhm_pct: number;
}

export function thoriumPeakPoints(results: readonly CalResult[]): ThoriumPeakPoint[] {
  return byEntry(results)
    .flat()
    .filter((r) => r.kind === 'th_peak' && r.th_peak !== null)
    .map((r) => ({ daily_entry_id: calGroupKey(r), th_peak: r.th_peak ?? 0, fwhm_pct: r.fwhm_pct ?? 0 }));
}

/** A hand-sample position relative to the first one (TJR "Hand Sample Calibration Positions"), in metres. */
export interface HandSamplePosition {
  readonly dE: number;
  readonly dN: number;
  readonly distanceM: number;
  /** Further than `GROUND_CAL_POSITION_TOLERANCE_M` from the first position. */
  readonly error: boolean;
}

export function handSamplePositions(
  stats: GroundCalStats,
  toleranceM: number = GROUND_CAL_POSITION_TOLERANCE_M,
): HandSamplePosition[] {
  const placed = stats.rows.filter(
    (r): r is GroundCalRow & { easting: number; northing: number } =>
      r.easting !== null && r.northing !== null,
  );
  const first = placed[0];
  if (!first) return [];
  return placed.map((r) => {
    const dE = r.easting - first.easting;
    const dN = r.northing - first.northing;
    const distanceM = Math.hypot(dE, dN);
    return { dE, dN, distanceM, error: distanceM > toleranceM };
  });
}

/**
 * The test line table as TJR prints it (Progress Operations Report "Test Lines" page; MB's R62 for the Calibration
 * screen): a row per group (`keyOf`: the day for TJR's page) with the group's 801 and 802 side by side; a group with
 * two 801s or two 802s gives a row for each, in order. A missing 801 or 802 is null.
 */
export interface TestLinePair {
  readonly key: string;
  readonly t801: TestLineRow | null;
  readonly t802: TestLineRow | null;
}

export function testLinePairs(stats: TestLineStats, keyOf: (row: TestLineRow) => string): TestLinePair[] {
  const groups = new Map<string, { t801: TestLineRow[]; t802: TestLineRow[] }>();
  for (const r of stats.rows) {
    const k = keyOf(r);
    const g = groups.get(k) ?? { t801: [], t802: [] };
    (r.line === 801 ? g.t801 : g.t802).push(r);
    groups.set(k, g);
  }
  const out: TestLinePair[] = [];
  for (const [key, g] of groups)
    for (let i = 0; i < Math.max(g.t801.length, g.t802.length); i++)
      out.push({ key, t801: g.t801[i] ?? null, t802: g.t802[i] ?? null });
  return out;
}

/** The mean Th of the 801 runs and of the 802 runs (the test line table's footer, R62); null without any. */
export function testLineAverages(stats: TestLineStats): {
  readonly avg801: number | null;
  readonly avg802: number | null;
  readonly count801: number;
  readonly count802: number;
} {
  const of = (line: 801 | 802): number[] => stats.rows.filter((r) => r.line === line).map((r) => r.values.th);
  const mean = (v: readonly number[]): number | null =>
    v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  const a = of(801);
  const b = of(802);
  return { avg801: mean(a), avg802: mean(b), count801: a.length, count802: b.length };
}
